<template>
  <div class="bank-accounts">
    <Actions :items="actionsHeader" :right="true" />
    <MobileTable
      v-model:selection="store.state.bankAccount.selectedBankAccounts"
      :label="listLabel"
      :columns="columns"
      :data="tableRows"
    />
    <DesktopTable
      v-model:selection="store.state.bankAccount.selectedBankAccounts"
      :label="listLabel"
      :columns="columns"
      :data="tableRows"
    />
    <p v-if="showEmptyState" class="empty-note">No hay cuentas bancarias para mostrar.</p>
    <Actions :items="actionsFooter" />
  </div>
</template>

<script>
import { computed, reactive, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import { openMode, actions } from "../../constants";

const formatBankName = (item) => {
  const bank = item.bank?.name ? ` - ${item.bank.name}` : "";
  return `${item.accountNumber}${bank}`;
};

export default {
  components: { Actions, MobileTable, DesktopTable },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const tableRows = computed(() =>
      (store.state.bankAccount.bankAccounts || []).map((item) => ({
        id: item.id,
        account: formatBankName(item),
        document: item.document || "",
        currency: item.currency?.code || item.currency?.name || "",
        initialBalance: Number(item.initialBalance ?? 0),
        startDate: item.startDate || "",
      }))
    );

    const listLabel = computed(() => `${route.name || "Cuentas bancarias"} (${tableRows.value.length})`);

    const columns = reactive([
      { field: "account", header: "Cuenta", sort: true },
      { field: "document", header: "Documento", sort: true },
      { field: "currency", header: "Moneda", sort: true },
      { field: "initialBalance", header: "Saldo inicial", currency: true, sort: true },
      { field: "startDate", header: "Fecha de inicio", date: true, sort: true },
    ]);

    const showEmptyState = computed(
      () => !store.state.loading && Boolean(buildingId.value) && !tableRows.value.length
    );

    const notify = (type, message) => {
      store.commit("setToast", { type, message });
    };

    const ensureBreadcrumb = () => {
      const breadcrumb = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!breadcrumb) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const loadBankAccounts = async () => {
      if (!buildingId.value) return;
      const response = await store.dispatch(actions.bankaccountActions.findAll, {
        buildingId: buildingId.value,
      });
      if (!response?.ok && response?.message) {
        notify("error", response.message);
      }
    };

    const requireSelection = () => {
      if (store.state.bankAccount.selectedBankAccounts.length !== 1) {
        notify("warn", "Debe seleccionar una cuenta bancaria");
        return false;
      }
      return true;
    };

    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/contabilidad/cuenta-bancaria/nuevo");
    };

    const onModify = () => {
      if (!requireSelection()) return;
      store.commit("setOpenMode", openMode.MODIFY);
      router.push("/contabilidad/cuenta-bancaria/modificar");
    };

    const onDownloadExcel = async () => {
      if (!tableRows.value.length) {
        notify("warn", "No hay datos para exportar");
        return;
      }
      store.commit("setLoading", true);
      try {
        const request = {
          fileName: "cuentas-bancarias",
          headers: columns.map((column) => column.header),
          data: tableRows.value.map((row) => [
            row.account,
            row.document,
            row.currency,
            row.initialBalance,
            row.startDate,
          ]),
        };
        await store.dispatch(actions.bankaccountActions.downloadExcel, request);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const actionsHeader = reactive([
      {
        name: "Descargar Excel",
        action: onDownloadExcel,
        hidden: false,
      },
    ]);

    const actionsFooter = reactive([
      {
        name: "Nueva",
        action: onCreate,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/cuenta-bancaria/nuevo"),
      },
      {
        name: "Modificar",
        action: onModify,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/cuenta-bancaria/modificar"),
      },
    ]);

    onMounted(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedBankAccounts", []);
      ensureBreadcrumb();
      try {
        await loadBankAccounts();
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        store.commit("setSelectedBankAccounts", []);
        try {
          await loadBankAccounts();
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    return {
      store,
      columns,
      tableRows,
      actionsHeader,
      actionsFooter,
      listLabel,
      showEmptyState,
    };
  },
};
</script>

<style scoped>
.bank-accounts {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
}

.empty-note {
  margin: 0 1rem;
  text-align: center;
  color: #64748b;
}
</style>
